import { PlusCircle } from "feather-icons-react/build/IconComponents";
import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { useToast } from "../../../Toaster/ToastContext";
import PropTypes from "prop-types";
import { apiClient } from "../../../services/apiClient";

export default function AddUsers({ user, onSave }) {
  const { showToast } = useToast();
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const [branches, setBranches] = useState([{ value: "", label: "Choose" }]);
  const [roles, setRoles] = useState([{ value: "", label: "Choose" }]);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    role_id: "",
    branch_id: "",
    business_id: "",
    status: "active",
    password: "",
    password_confirmation: "",
  });
  const [businesses, setBusinesses] = useState([
    { value: "", label: "Choose" },
  ]);
  const [userType, setUserType] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    phone: "",
    role_id: "",
    branch_id: "",
    email: "",
    business_id: "",
    status: "",
    password: "",
    password_confirmation: "",
    user_image: "",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleTogglePassword = () => {
    setShowPassword((prevShowPassword) => !prevShowPassword);
  };

  const handleToggleConfirmPassword = () => {
    setConfirmPassword((prevShowPassword) => !prevShowPassword);
  };

  // Detect modal open
  useEffect(() => {
    const modalElement = document.getElementById("add-units");
    const handleModalShow = () => {
      setIsModalOpen(true);
      // Initialize form data when modal opens
      if (user) {
        const branchId = user.branch?.id ? user.branch.id.toString() : "";
        const roleId = user.roles?.id ? user.roles.id.toString() : "";
        const businessId = user.business?.id ? user.business.id.toString() : "";
        setFormData({
          id: user.id,
          name: user.name || "",
          user_image: user.user_image || "",
          phone: user.phone || "",
          email: user.email || "",
          role_id: roleId,
          password: user.password || "",
          password_confirmation: user.password_confirmation || "",
          branch_id: branchId,
          status: user.status || "active",
          business_id: businessId,
        });
        if (user.user_image) {
          setImagePreview(user.user_image);
          setImageFile(null);
          setIsImageRemoved(false);
        } else {
          setImagePreview(null);
          setImageFile(null);
          setIsImageRemoved(false);
        }
      } else {
        setFormData({
          name: "",
          user_image: "",
          phone: "",
          email: "",
          role_id: "",
          password: "",
          password_confirmation: "",
          branch_id: "",
          status: "active",
          business_id: "",
        });
        setImagePreview(null);
        setImageFile(null);
        setIsImageRemoved(false);
      }
      setErrors({
        name: "",
        phone: "",
        role_id: "",
        branch_id: "",
        email: "",
        business_id: "",
        status: "",
        password: "",
        password_confirmation: "",
        user_image: "",
      });
      // Reset dropdown refs to force reload
      businessesRef.current = false;
      branchesRef.current = false;
      rolesRef.current = false;
    };

    modalElement.addEventListener("shown.bs.modal", handleModalShow);

    return () => {
      modalElement.removeEventListener("shown.bs.modal", handleModalShow);
    };
  }, [user]);

  // Reset form when modal closes
  useEffect(() => {
    const modalElement = document.getElementById("add-units");
    const handleModalClose = () => {
      setIsModalOpen(false);
      // Reset form to original submitted data
      if (user) {
        const branchId = user.branch?.id ? user.branch.id.toString() : "";
        const roleId = user.roles?.id ? user.roles.id.toString() : "";
        const businessId = user.business?.id ? user.business.id.toString() : "";
        setFormData({
          id: user.id,
          name: user.name || "",
          user_image: user.user_image || "",
          phone: user.phone || "",
          email: user.email || "",
          role_id: roleId,
          password: user.password || "",
          password_confirmation: user.password_confirmation || "",
          branch_id: branchId,
          status: user.status || "active",
          business_id: businessId,
        });
        if (user.user_image) {
          setImagePreview(user.user_image);
          setImageFile(null);
          setIsImageRemoved(false);
        } else {
          setImagePreview(null);
          setImageFile(null);
          setIsImageRemoved(false);
        }
      } else {
        setFormData({
          name: "",
          user_image: "",
          phone: "",
          email: "",
          role_id: "",
          password: "",
          password_confirmation: "",
          branch_id: "",
          status: "active",
          business_id: "",
        });
        setImagePreview(null);
        setImageFile(null);
        setIsImageRemoved(false);
      }
      setErrors({
        name: "",
        phone: "",
        role_id: "",
        branch_id: "",
        email: "",
        business_id: "",
        status: "",
        password: "",
        password_confirmation: "",
        user_image: "",
      });
      // Reset dropdown refs
      businessesRef.current = false;
      branchesRef.current = false;
      rolesRef.current = false;
    };

    modalElement.addEventListener("hidden.bs.modal", handleModalClose);

    return () => {
      modalElement.removeEventListener("hidden.bs.modal", handleModalClose);
    };
  }, [user]);

  const businessesRef = React.useRef(false);
  const branchesRef = React.useRef(false);
  const rolesRef = React.useRef(false);
  const isMounted = React.useRef(true);

  const loadBusinesses = useCallback(
    async (force = false) => {
      if (businessesRef.current && !force) return;
      try {
        console.log("Fetching businesses..."); // Debug log
        const apiData = await apiClient.get("/get-all-business");
        console.log("Business API response:", apiData); // Debug log
        if (!isMounted.current) return;
        if (apiData.success && Array.isArray(apiData.data)) {
          const formattedBusinesses = [
            { value: "", label: "Choose" },
            ...apiData.data.map((business) => ({
              value: business.id.toString(),
              label: business.name,
            })),
          ];
          setBusinesses(() => {
            const userBusiness = user?.business;
            if (userBusiness && userBusiness.id && isModalOpen) {
              const userBusinessOption = {
                value: userBusiness.id.toString(),
                label: userBusiness.name,
              };
              console.log("Setting user business:", userBusinessOption); // Debug log
              if (
                !formattedBusinesses.some(
                  (b) => b.value === userBusinessOption.value
                )
              ) {
                return [userBusinessOption, ...formattedBusinesses];
              }
            }
            console.log("Setting businesses:", formattedBusinesses); // Debug log
            return formattedBusinesses;
          });
          businessesRef.current = true;
        } else {
          showToast(
            "Failed to fetch businesses",
            "error",
            "Business Fetch Operation"
          );
        }
      } catch (error) {
        if (isMounted.current) {
          console.error("Business fetch error:", error); // Debug log
          showToast(
            "Error fetching businesses: Please contact the support team.",
            "error",
            "Business Fetch Operation"
          );
        }
      }
    },
    [showToast, user, isModalOpen]
  );

  const loadBranches = useCallback(
    async (force = false, businessId = null) => {
      if (branchesRef.current && !force) return;
      try {
        console.log("Fetching branches for businessId:", businessId); // Debug log
        let apiData;
        if (businessId) {
          apiData = await apiClient.post("/get-all-branch", {
            business_id: businessId,
          });
        } else {
          apiData = await apiClient.get("/get-all-branch");
        }
        console.log("Branches API response:", apiData); // Debug log
        if (apiData && apiData.success && Array.isArray(apiData.data)) {
          const formattedBranches = [
            { value: "", label: "Choose" },
            ...apiData.data.map((branch) => ({
              value: branch.id.toString(),
              label: branch.name,
            })),
          ];
          setBranches(() => {
            const userBranch = user?.branch;
            if (
              userBranch &&
              userBranch.id &&
              businessId === user?.business?.id?.toString() &&
              isModalOpen
            ) {
              const userBranchOption = {
                value: userBranch.id.toString(),
                label: userBranch.name,
              };
              if (
                !formattedBranches.some(
                  (b) => b.value === userBranchOption.value
                )
              ) {
                return [userBranchOption, ...formattedBranches];
              }
            }
            return formattedBranches;
          });
          branchesRef.current = true;
        } else {
          setBranches([{ value: "", label: "Choose" }]);
          showToast(
            "Failed to fetch branches",
            "error",
            "Branch Fetch Operation"
          );
        }
      } catch (error) {
        if (isMounted.current) {
          console.error("Branches fetch error:", error); // Debug log
          showToast(
            "Error fetching branches: Please contact the support team.",
            "error",
            "Branch Fetch Operation"
          );
        }
      }
    },
    [showToast, user, isModalOpen]
  );

  const loadRoles = useCallback(
    async (force = false, businessId = null) => {
      if (rolesRef.current && !force) return;
      try {
        console.log("Fetching roles for businessId:", businessId); // Debug log
        let apiData;
        if (businessId) {
          apiData = await apiClient.post("/get-all-permission", {
            business_id: businessId,
          });
        } else {
          apiData = await apiClient.get("/get-all-permission");
        }
        console.log("Roles API response:", apiData); // Debug log
        if (apiData.success && Array.isArray(apiData.data)) {
          const formattedRoles = [
            { value: "", label: "Choose" },
            ...apiData.data.map((role) => ({
              value: role.id.toString(),
              label: role.roles,
            })),
          ];
          setRoles(() => {
            const userRole = user?.roles;
            if (
              userRole &&
              userRole.id &&
              businessId === user?.business?.id?.toString() &&
              isModalOpen
            ) {
              const userRoleOption = {
                value: userRole.id.toString(),
                label: userRole.name,
              };
              if (
                !formattedRoles.some((r) => r.value === userRoleOption.value)
              ) {
                return [userRoleOption, ...formattedRoles];
              }
            }
            return formattedRoles;
          });
          rolesRef.current = true;
        } else {
          setRoles([{ value: "", label: "Choose" }]);
          showToast("Failed to fetch roles", "error", "Role Fetch Operation");
        }
      } catch (error) {
        if (isMounted.current) {
          console.error("Roles fetch error:", error); // Debug log
          showToast(
            "Error fetching roles: Please contact the support team.",
            "error",
            "Role Fetch Operation"
          );
        }
      }
    },
    [showToast, user, isModalOpen]
  );

  // Load dropdown data when modal opens
  // useEffect(() => {
  //   if (!isModalOpen) return;
  //   console.log("Modal opened, loading dropdown data..."); // Debug log
  //   const storedUserType = localStorage.getItem("user_type");
  //   setUserType(storedUserType || "");
  //   console.log("User type:", storedUserType); // Debug log
  //   if (storedUserType === "owner" || user?.business?.id) {
  //     loadBusinesses(true);
  //     if (user?.business?.id) {
  //       const businessId = user.business.id.toString();
  //       loadBranches(true, businessId);
  //       loadRoles(true, businessId);
  //     }
  //   }
  //   return () => {
  //     isMounted.current = false;
  //   };
  // }, [isModalOpen, user, loadBusinesses, loadBranches, loadRoles]);

  useEffect(() => {
    if (!isModalOpen) return;
    console.log("Modal opened, loading dropdown data...");

    const storedUserType = localStorage.getItem("user_type");
    const storedBusinessId = localStorage.getItem("business_id");
    setUserType(storedUserType || "");
    console.log("User type:", storedUserType);

    if (storedUserType === "owner" || user?.business?.id) {
      // Owner/Admin → Load businesses first
      loadBusinesses(true);

      if (user?.business?.id) {
        const businessId = user.business.id.toString();
        loadBranches(true, businessId);
        loadRoles(true, businessId);
      }
    } else if (storedUserType === "business" && storedBusinessId) {
      // Business login → directly fetch branches & roles
      loadBranches(true, storedBusinessId);
      loadRoles(true, storedBusinessId);
    }

    return () => {
      isMounted.current = false;
    };
  }, [isModalOpen, user, loadBusinesses, loadBranches, loadRoles]);

  AddUsers.propTypes = {
    user: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      user_image: PropTypes.string,
      phone: PropTypes.string,
      email: PropTypes.string,
      password: PropTypes.string,
      password_confirmation: PropTypes.string,
      status: PropTypes.oneOf(["active", "inactive"]),
      branch: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      roles: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
      business: PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        name: PropTypes.string,
      }),
    }),
    onSave: PropTypes.func,
  };

  const validateField = (name, value) => {
    const isEditMode = !!formData.id;
    const password = formData.password || "";

    switch (name) {
      case "name":
        return value?.trim() ? "" : "User name is required";

      case "phone":
        if (!value?.trim()) return "Phone is required";
        if (!/^[0-9-]+$/.test(value))
          return "Phone number can only contain numbers and hyphens";
        return "";

      case "email":
        return value?.trim()
          ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
            ? ""
            : "Invalid email format"
          : "Email is required";

      case "role_id":
        return value ? "" : "Role is required";

      case "branch_id":
        return value ? "" : "Branch is required";

      case "business_id":
        return userType === "owner" && !value ? "Business is required" : "";

      case "password":
        if (!isEditMode) {
          return value ? "" : "Password is required";
        } else {
          return value && value.length < 6
            ? "Password must be at least 6 characters"
            : "";
        }

      case "password_confirmation":
        if (!isEditMode) {
          if (!value) return "Confirm password is required";
          return value === password ? "" : "Passwords do not match";
        } else {
          if (password && !value) return "Confirm password is required";
          return password && value !== password ? "Passwords do not match" : "";
        }

      case "status":
        return value ? "" : "Status is required";

      case "user_image":
        return !(imagePreview || imageFile) ? "Image is required" : "";

      default:
        return "";
    }
  };

  const validateForm = () => {
    const newErrors = {
      user_image: validateField("user_image", formData.user_image),
      name: validateField("name", formData.name),
      phone: validateField("phone", formData.phone),
      email: validateField("email", formData.email),
      role_id: validateField("role_id", formData.role_id),
      branch_id: validateField("branch_id", formData.branch_id),
      business_id: validateField("business_id", formData.business_id),
      password: validateField("password", formData.password),
      password_confirmation: validateField(
        "password_confirmation",
        formData.password_confirmation
      ),
      status: validateField("status", formData.status),
    };

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
        setImageFile(file);
        setIsImageRemoved(false);
        setErrors((prev) => ({ ...prev, user_image: "" }));
      };
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    setImageFile(null);
    setIsImageRemoved(true);
    setErrors((prev) => ({ ...prev, user_image: "Image is required" }));
  };

  const handleBusinessChange = (selectedOption) => {
    const businessId = selectedOption.value;
    setFormData((prev) => ({
      ...prev,
      business_id: businessId,
      branch_id: "",
      role_id: "",
    }));
    setErrors((prev) => ({
      ...prev,
      business_id: "",
      branch_id: "",
      role_id: "",
    }));
    setBranches([{ value: "", label: "Choose" }]);
    setRoles([{ value: "", label: "Choose" }]);
    branchesRef.current = false;
    rolesRef.current = false;
    if (businessId) {
      loadBranches(true, businessId);
      loadRoles(true, businessId);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const formDataToSend = new FormData();
        const userType = localStorage.getItem("user_type");
        const businessId = localStorage.getItem("business_id");
        formDataToSend.append("name", formData.name || "");
        formDataToSend.append("phone", formData.phone || "");
        formDataToSend.append("email", formData.email || "");
        formDataToSend.append("role_id", formData.role_id || "");
        formDataToSend.append("password", formData.password || "");
        formDataToSend.append(
          "password_confirmation",
          formData.password_confirmation || ""
        );
        formDataToSend.append("branch_id", formData.branch_id || "");
        formDataToSend.append("status", formData.status || "active");
        const selectedRole = roles.find(
          (role) => String(role.value) === String(formData.role_id)
        );
        const userTypeValue = selectedRole ? selectedRole.label : "";
        formDataToSend.append("user_type", userTypeValue);
        if (userType === "owner") {
          formDataToSend.append(
            "business_id",
            formData.business_id || businessId
          );
        }
        if (isImageRemoved) {
          formDataToSend.append("user_image", "");
        } else if (imageFile) {
          formDataToSend.append("user_image", imageFile, imageFile.name);
        } else if (user && user.user_image && !isImageRemoved) {
          formDataToSend.append("user_image", user.user_image);
        } else {
          formDataToSend.append("user_image", "");
        }
        if (formData.id) {
          formDataToSend.append("id", formData.id);
        }
        console.log("Submitting form data:", formDataToSend); // Debug log
        const Apidata = await apiClient.post(
          "/store-update-business-user",
          formDataToSend,
          true
        );
        console.log("Submit API response:", Apidata); // Debug log
        if (Apidata.success) {
          onSave && onSave();
          document
            .getElementById("add-units")
            .querySelector('[data-bs-dismiss="modal"]')
            .click();
          showToast(Apidata.message, "success", "Business User Operation!");
        } else {
          showToast(
            Apidata.message || "Failed to save user",
            "error",
            "Business Users Operation!"
          );
        }
      } catch (error) {
        console.error("Submit error:", error); // Debug log
        showToast(
          error.message ||
            "An unexpected error occurred: Please contact the support team.",
          "error",
          "Business Users Operation!"
        );
      }
    }
  };

  return (
    <div>
      <div className="modal fade" id="add-units">
        <div className="modal-dialog modal-dialog-centered custom-modal-two">
          <div className="modal-content">
            <div className="page-wrapper-new p-0">
              <div className="content">
                <div className="modal-header border-0 custom-modal-header d-flex justify-content-between align-items-center">
                  <div className="page-title">
                    <h4>{user ? "Update User" : "Add User"}</h4>
                  </div>
                  <button
                    type="button"
                    className="close"
                    data-bs-dismiss="modal"
                    aria-label="Close"
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <div className="modal-body custom-modal-body">
                  <form>
                    <div className="row">
                      <div className="mb-3">
                        <label className="form-label">Image</label>
                        <div className="profile-pic-upload mb-3">
                          <div
                            className={`profile-pic user-pic ${
                              errors.user_image ? "is-invalid" : ""
                            }`}
                            style={{
                              position: "relative",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: "120px",
                              border: "1px dashed #ccc",
                              borderRadius: "8px",
                              cursor: "pointer",
                              overflow: "hidden",
                            }}
                          >
                            {imagePreview ? (
                              <div
                                style={{
                                  position: "relative",
                                  width: "100%",
                                  height: "100%",
                                }}
                              >
                                <img
                                  src={imagePreview}
                                  alt="User"
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "cover",
                                    borderRadius: "8px",
                                  }}
                                />
                                <button
                                  type="button"
                                  style={{
                                    position: "absolute",
                                    top: "5px",
                                    right: "5px",
                                    background: "#fff",
                                    border: "1px solid #ccc",
                                    borderRadius: "50%",
                                    width: "26px",
                                    height: "26px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    fontSize: "16px",
                                    lineHeight: "1",
                                    padding: 0,
                                  }}
                                  onClick={() => {
                                    handleRemoveImage();
                                    document.getElementById(
                                      "user-image-upload"
                                    ).value = "";
                                  }}
                                >
                                  ×
                                </button>
                                <label
                                  htmlFor="user-image-upload"
                                  style={{
                                    position: "absolute",
                                    bottom: "5px",
                                    right: "5px",
                                    background: "#ff9f43",
                                    color: "#fff",
                                    borderRadius: "50%",
                                    width: "28px",
                                    height: "28px",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    fontSize: "14px",
                                  }}
                                >
                                  ✎
                                </label>
                              </div>
                            ) : (
                              <label
                                htmlFor="user-image-upload"
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  width: "100%",
                                  height: "100%",
                                  cursor: "pointer",
                                  color: "#666",
                                  fontSize: "14px",
                                  textAlign: "center",
                                }}
                              >
                                <PlusCircle
                                  size={15}
                                  style={{ marginBottom: "4px" }}
                                />
                                Add Image
                              </label>
                            )}
                          </div>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            id="user-image-upload"
                            style={{ display: "none" }}
                          />
                        </div>
                        {errors.user_image && (
                          <div className="invalid-feedback d-block">
                            {errors.user_image}
                          </div>
                        )}
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>User Name</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.name ? "is-invalid" : ""
                            }`}
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            key="name"
                          />
                          {errors.name && (
                            <div className="invalid-feedback">
                              {errors.name}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Phone</label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.phone ? "is-invalid" : ""
                            } no-spinner`}
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            key="phone"
                          />
                          {errors.phone && (
                            <div className="invalid-feedback">
                              {errors.phone}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Email</label>
                          <input
                            type="email"
                            className={`form-control ${
                              errors.email ? "is-invalid" : ""
                            }`}
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            key="email_input"
                          />
                          {errors.email && (
                            <div className="invalid-feedback">
                              {errors.email}
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Business */}
                      {userType === "owner" && (
                        <div className="col-lg-6">
                          <div className="input-blocks">
                            <label>Business</label>
                            <Select
                              className={`select ${
                                errors.business_id ? "is-invalid" : ""
                              }`}
                              options={businesses}
                              placeholder="Choose Business"
                              value={businesses.find(
                                (option) =>
                                  option.value === formData.business_id
                              )}
                              onChange={handleBusinessChange}
                              key="business_select"
                              styles={{
                                control: (base, state) => ({
                                  ...base,
                                  minHeight: "38px",
                                  height: "38px",
                                  borderColor: state.isFocused
                                    ? "#86b7fe"
                                    : base.borderColor,
                                  boxShadow: state.isFocused
                                    ? "0 0 0 0.25rem rgba(13,110,253,.25)"
                                    : base.boxShadow,
                                  "&:hover": {
                                    borderColor: state.isFocused
                                      ? "#86b7fe"
                                      : base.borderColor,
                                  },
                                }),
                                valueContainer: (base) => ({
                                  ...base,
                                  height: "38px",
                                  padding: "0 8px",
                                }),
                                input: (base) => ({
                                  ...base,
                                  margin: 0,
                                  padding: 0,
                                }),
                                indicatorsContainer: (base) => ({
                                  ...base,
                                  height: "38px",
                                }),
                              }}
                            />
                            {errors.business_id && (
                              <div className="invalid-feedback">
                                {errors.business_id}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Branch */}
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Branch</label>
                          <Select
                            className={`select ${
                              errors.branch_id ? "is-invalid" : ""
                            }`}
                            options={branches}
                            placeholder="Choose Branch"
                            value={branches.find(
                              (option) => option.value === formData.branch_id
                            )}
                            onChange={(selectedOption) =>
                              handleInputChange({
                                target: {
                                  name: "branch_id",
                                  value: selectedOption.value,
                                },
                              })
                            }
                            key="branch_select"
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: "38px",
                                height: "38px",
                                borderColor: state.isFocused
                                  ? "#86b7fe"
                                  : base.borderColor,
                                boxShadow: state.isFocused
                                  ? "0 0 0 0.25rem rgba(13,110,253,.25)"
                                  : base.boxShadow,
                                "&:hover": {
                                  borderColor: state.isFocused
                                    ? "#86b7fe"
                                    : base.borderColor,
                                },
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                height: "38px",
                                padding: "0 8px",
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                              }),
                              indicatorsContainer: (base) => ({
                                ...base,
                                height: "38px",
                              }),
                            }}
                          />
                          {errors.branch_id && (
                            <div className="invalid-feedback">
                              {errors.branch_id}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Role */}
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Role</label>
                          <Select
                            className={`select ${
                              errors.role_id ? "is-invalid" : ""
                            }`}
                            options={roles}
                            placeholder="Choose Role"
                            value={roles.find(
                              (option) => option.value === formData.role_id
                            )}
                            onChange={(selectedOption) =>
                              handleInputChange({
                                target: {
                                  name: "role_id",
                                  value: selectedOption.value,
                                },
                              })
                            }
                            key="role_select"
                            styles={{
                              control: (base, state) => ({
                                ...base,
                                minHeight: "38px",
                                height: "38px",
                                borderColor: state.isFocused
                                  ? "#86b7fe"
                                  : base.borderColor,
                                boxShadow: state.isFocused
                                  ? "0 0 0 0.25rem rgba(13,110,253,.25)"
                                  : base.boxShadow,
                                "&:hover": {
                                  borderColor: state.isFocused
                                    ? "#86b7fe"
                                    : base.borderColor,
                                },
                              }),
                              valueContainer: (base) => ({
                                ...base,
                                height: "38px",
                                padding: "0 8px",
                              }),
                              input: (base) => ({
                                ...base,
                                margin: 0,
                                padding: 0,
                              }),
                              indicatorsContainer: (base) => ({
                                ...base,
                                height: "38px",
                              }),
                            }}
                          />
                          {errors.role_id && (
                            <div className="invalid-feedback">
                              {errors.role_id}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Password</label>
                          <div className="pass-group">
                            <input
                              type={showPassword ? "text" : "password"}
                              className={`pass-input ${
                                errors.password ? "is-invalid" : ""
                              }`}
                              placeholder="Enter your password"
                              name="password"
                              value={formData.password}
                              onChange={handleInputChange}
                              key="password_input"
                            />
                            <span
                              className={`fas toggle-password ${
                                showPassword ? "fa-eye" : "fa-eye-slash"
                              }`}
                              onClick={handleTogglePassword}
                            />
                            {errors.password && (
                              <div className="invalid-feedback">
                                {errors.password}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Confirm Password</label>
                          <div className="pass-group">
                            <input
                              type={showConfirmPassword ? "text" : "password"}
                              className={`pass-input ${
                                errors.password_confirmation ? "is-invalid" : ""
                              }`}
                              placeholder="Enter your password"
                              name="password_confirmation"
                              value={formData.password_confirmation}
                              onChange={handleInputChange}
                              key="password_confirmation"
                            />
                            <span
                              className={`fas toggle-password ${
                                showConfirmPassword ? "fa-eye" : "fa-eye-slash"
                              }`}
                              onClick={handleToggleConfirmPassword}
                            />
                            {errors.password_confirmation && (
                              <div className="invalid-feedback">
                                {errors.password_confirmation}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <div className="input-blocks">
                          <label>Status</label>
                          <select
                            className={`form-select ${
                              errors.status ? "is-invalid" : ""
                            }`}
                            name="status"
                            value={formData.status}
                            onChange={handleInputChange}
                          >
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          {errors.status && (
                            <div className="invalid-feedback">
                              {errors.status}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="modal-footer-btn">
                      <button
                        type="button"
                        className="btn btn-cancel me-2"
                        data-bs-dismiss="modal"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-submit"
                        onClick={handleSubmit}
                      >
                        {user ? "Update" : "Submit"}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

AddUsers.defaultProps = {
  user: null,
};
